import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, ChevronUp, Briefcase, Megaphone, Mountain, Headphones } from 'lucide-react';
import { careersAPI } from '../services/api';
import { useSiteSettings } from '../hooks/useContent';
import { usePageSeo } from '../hooks/usePageSeo';
import { buildOrganizationSchema, buildBreadcrumbSchema } from '../utils/seo';
import LoadingIndicator from '../components/LoadingIndicator';
import { optimizeImageUrl, IMAGE_DISPLAY_WIDTH } from '../utils/optimizeImageUrl.js';
import './Careers.css';


const CATEGORY_ICONS = {
  operations: Briefcase,
  'digital_marketing': Megaphone,
  'field_instructors': Mountain,
  'sales_support': Headphones,
  default: Briefcase
};

function getCategoryIcon(iconKey) {
  const key = (iconKey || '').toLowerCase().replace(/\s+/g, '_');
  return CATEGORY_ICONS[key] || CATEGORY_ICONS.default;
}

const defaultData = {
  hero: { title: 'Careers At Invincible', subtitle: '', imageUrl: '' },
  whyJoin: { title: 'Why Join Invincible?', description: '', bullets: [], imageUrl: '' },
  lifeAt: { title: 'Life at Invincible', subtitle: 'Passion, purpose, and plenty of adventure.', images: [] },
  positions: { title: 'Open Positions', subtitle: 'Find the role that fits your passion and skills.', categories: [] }
};

const Careers = () => {
  const [data, setData] = useState(defaultData);
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const { settings } = useSiteSettings();

  const publicBase =
    String(settings.site_public_url || '').replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const brand = settings.site_brand_name || 'Invincible NGO';

  const careersSchema = useMemo(() => {
    const org = buildOrganizationSchema(settings);
    const breadcrumbs = buildBreadcrumbSchema([
      { name: 'Careers', url: '/careers' }
    ], publicBase);
    return {
      '@context': 'https://schema.org',
      '@graph': [org, breadcrumbs]
    };
  }, [settings, publicBase]);

  usePageSeo({
    title: `${data.hero?.title || 'Careers At Invincible'} | ${brand}`,
    description: data.hero?.subtitle || 'Join our team of adventure leaders, instructors, and changemakers at Invincible NGO.',
    canonical: publicBase ? `${publicBase}/careers` : undefined,
    image: data.hero?.imageUrl || undefined,
    schema: careersSchema
  });


  useEffect(() => {
    let cancelled = false;
    careersAPI.getCareers()
      .then((res) => {
        const payload = res.data?.data || res.data;
        if (payload && !cancelled) setData({ ...defaultData, ...payload });
      })
      .catch(() => { if (!cancelled) setData(defaultData); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const { hero, whyJoin, lifeAt, positions } = data;
  const categories = positions?.categories || [];

  if (loading) {
    return (
      <div className="careers-page">
        <div className="blog-hero-banner careers-hero-banner-loading">
          <div className="blog-hero-inner">
            <LoadingIndicator label="Loading..." />
          </div>
        </div>
      </div>
    );
  }

  // Split title so we can highlight "Invincible"
  const heroTitleParts = (hero.title || 'Careers At Invincible').split(/(\bInvincible\b)/i);
  if (heroTitleParts.length === 1) heroTitleParts.push('', hero.title || '');

  const heroImage = optimizeImageUrl(String(hero.imageUrl || '').trim(), {
    width: IMAGE_DISPLAY_WIDTH.hero
  });
  const heroClassName = heroImage
    ? 'blog-hero-banner blog-hero-banner-with-image careers-page-hero-band'
    : 'blog-hero-banner careers-page-hero-band';

  return (
    <div className="careers-page">
      <section
        className={heroClassName}
        style={
          heroImage
            ? {
                backgroundImage: `linear-gradient(135deg, rgba(15,23,42,0.65) 0%, rgba(15,23,42,0.45) 50%, rgba(15,23,42,0.35) 100%), url(${heroImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }
            : undefined
        }
      >
        <div className="blog-hero-inner">
          <h1 className="blog-hero-title careers-hero-title">
            {heroTitleParts.map((part, i) =>
              /invincible/i.test(part) ? <span key={i} className="careers-hero-title-accent">{part}</span> : part
            )}
          </h1>
          <p className="blog-hero-subtitle careers-hero-subtitle">{hero.subtitle}</p>
        </div>
      </section>

      <div className="careers-content">
        <section className="careers-why-section">
          <div className="careers-why-grid">
            <div className="careers-why-text">
              <h2 className="careers-why-title">{whyJoin.title}</h2>
              <div className="careers-why-underline" />
              <p className="careers-why-desc">{whyJoin.description}</p>
              <ul className="careers-why-bullets">
                {(whyJoin.bullets || []).map((item, i) => (
                  <li key={i}>
                    <Check size={20} className="careers-bullet-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="careers-why-visual">
              <div className="careers-why-image-card">
                {whyJoin.imageUrl ? (
                  <img
                    src={optimizeImageUrl(whyJoin.imageUrl, { width: IMAGE_DISPLAY_WIDTH.gallery })}
                    alt=""
                   loading="lazy" decoding="async" />
                ) : (
                  <div className="careers-why-illustration" />
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="careers-life-section">
          <h2 className="careers-life-title">{lifeAt.title}</h2>
          <p className="careers-life-subtitle">{lifeAt.subtitle}</p>
          <div className="careers-life-grid">
            {[...Array(8)].map((_, i) => {
              // Slot 7 (index 6) is the decorative block — must not reuse photos[5] or slot 6 repeats slot 5.
              const isBlockSlot = i === 6;
              const imageIndex = isBlockSlot ? -1 : i < 6 ? i : i - 1;
              const imgs = lifeAt.images || [];
              const url =
                imageIndex >= 0 && imageIndex < imgs.length ? String(imgs[imageIndex] || '').trim() : '';
              return (
                <div key={i} className={`careers-life-card careers-life-slot-${i + 1} ${isBlockSlot ? 'careers-life-block' : ''} ${!isBlockSlot && !url ? 'careers-life-placeholder' : ''}`}>
                  {!isBlockSlot && url ? (
                    <img
                      src={optimizeImageUrl(url, { width: IMAGE_DISPLAY_WIDTH.card })}
                      alt=""
                      loading="lazy"
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>

        <section className="careers-positions-section">
          <h2 className="careers-positions-title">{positions.title}</h2>
          <p className="careers-positions-subtitle">{positions.subtitle}</p>
          <div className="careers-accordion">
            {categories.map((cat) => {
              const Icon = getCategoryIcon(cat.icon);
              const jobs = cat.jobs || [];
              const isOpen = expandedCategory === cat.id;
              return (
                <div key={cat.id} className="careers-accordion-item">
                  <button
                    type="button"
                    className="careers-accordion-head"
                    onClick={() => setExpandedCategory(isOpen ? null : cat.id)}
                  >
                    <span className="careers-accordion-head-left">
                      <Icon size={22} className="careers-accordion-icon" />
                      <span>{cat.name}</span>
                    </span>
                    <span className="careers-accordion-head-right">
                      <span className="careers-accordion-meta">{jobs.length} Openings</span>
                      {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="careers-accordion-body">
                      {jobs.map((job) => (
                        <div key={job.id} className="careers-job-row">
                          <div>
                            <h3 className="careers-job-title">{job.title}</h3>
                            <p className="careers-job-meta">
                              {[job.location, job.employmentType].filter(Boolean).join(' • ')}
                            </p>
                          </div>
                          <Link to={`/careers/jobs/${job.id}`} className="careers-job-link">
                            View Details
                            <img
                              src="/assets/arrow-up-right.svg"
                              alt=""
                              className="careers-job-link-arrow"
                              width={16}
                              height={16}
                              aria-hidden
                            />
                          </Link>
                        </div>
                      ))}
                      {jobs.length === 0 && <p className="careers-no-jobs">No openings in this category.</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {categories.length === 0 && (
            <p className="careers-no-categories">No open positions at the moment. Check back soon!</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default Careers;
